import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { PageTitle, KPI, KPIStrip, Badge, Button, Card, EmptyState, Icons } from '@/shared/ui';

const LABEL_CLS = 'block text-sm font-medium text-stone-700 mb-1';
const INPUT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:bg-stone-50 disabled:text-stone-500';
const SELECT_CLS = 'h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';

const CVSetup = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [filterBuilding, setFilterBuilding] = useState('');
  const [filterZone, setFilterZone] = useState('');

  const { data: cameras = [], isLoading } = useQuery({
    queryKey: ['cameras', filterBuilding, filterZone],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterBuilding) params.append('building_id', filterBuilding);
      if (filterZone) params.append('zone', filterZone);
      const response = await api.get(`/api/cameras?${params.toString()}`);
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  const { data: zones = [] } = useQuery({
    queryKey: ['camera-zones'],
    queryFn: async () => {
      const response = await api.get('/api/camera-zones');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  const { data: detectionTypes = [] } = useQuery({
    queryKey: ['detection-types'],
    queryFn: async () => {
      const response = await api.get('/api/detection-types');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings'],
    queryFn: async () => {
      const response = await api.get('/api/properties/buildings');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  const { data: stats } = useQuery({
    queryKey: ['camera-stats'],
    queryFn: async () => {
      const response = await api.get('/api/cameras/stats/summary');
      return response.data;
    },
  });

  const createCameraMutation = useMutation({
    mutationFn: (data) => api.post('/api/cameras', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      queryClient.invalidateQueries(['camera-stats']);
      toast.success('Camera created successfully');
      setShowModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create camera');
    },
  });

  const updateCameraMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/api/cameras/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      queryClient.invalidateQueries(['camera-stats']);
      toast.success('Camera updated successfully');
      setShowModal(false);
      setEditingCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update camera');
    },
  });

  const deleteCameraMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/cameras/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      queryClient.invalidateQueries(['camera-stats']);
      toast.success('Camera deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete camera');
    },
  });

  const toggleCameraMutation = useMutation({
    mutationFn: (id) => api.post(`/api/cameras/${id}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries(['cameras']);
      queryClient.invalidateQueries(['camera-stats']);
      toast.success('Camera status updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to toggle camera');
    },
  });

  const handleEdit = (camera) => {
    setEditingCamera(camera);
    setShowModal(true);
  };

  const handleDelete = (camera) => {
    if (window.confirm(`Are you sure you want to delete camera "${camera.name}"?`)) {
      deleteCameraMutation.mutate(camera.camera_id);
    }
  };

  const handleToggle = (camera) => {
    toggleCameraMutation.mutate(camera.camera_id);
  };

  const getZoneIcon = (zone) => {
    const map = {
      entrance: <Icons.MapPin size={14} />,
      parking: <Icons.Truck size={14} />,
      fire_escape: <Icons.Fire size={14} />,
      hallway: <Icons.MapPin size={14} />,
      lobby: <Icons.User size={14} />,
    };
    return map[zone] || <Icons.MapPin size={14} />;
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="CV Camera Setup"
        subtitle="Configure cameras for computer vision monitoring"
        actions={
          <Button
            kind="primary"
            iconLeft={<Icons.Plus />}
            onClick={() => { setEditingCamera(null); setShowModal(true); }}
          >
            Add Camera
          </Button>
        }
      />

      {stats && (
        <div className="space-y-3">
          <KPIStrip columns={4}>
            <KPI label="Total Cameras" value={stats.total_cameras} icon={<Icons.Video />} accent="info" />
            <KPI label="Active" value={stats.active_cameras} icon={<Icons.Eye />} accent="success" />
            <KPI label="Online" value={stats.online_cameras} icon={<Icons.Wifi />} accent="success" />
            <KPI label="Offline" value={stats.offline_cameras} icon={<Icons.WifiOff />} accent="danger" />
          </KPIStrip>
          <KPIStrip columns={3}>
            <KPI label="LPR Enabled" value={stats.lpr_enabled} icon={<Icons.Truck />} accent="info" />
            <KPI label="Person Detection" value={stats.person_detection_enabled} icon={<Icons.User />} accent="info" />
            <KPI label="Fire Escape" value={stats.fire_escape_monitoring} icon={<Icons.Fire />} accent="warning" />
          </KPIStrip>
        </div>
      )}

      <Card padding={16}>
        <div className="flex flex-wrap gap-4">
          <select
            value={filterBuilding}
            onChange={(e) => setFilterBuilding(e.target.value)}
            className={SELECT_CLS}
          >
            <option value="">All Buildings</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            value={filterZone}
            onChange={(e) => setFilterZone(e.target.value)}
            className={SELECT_CLS}
          >
            <option value="">All Zones</option>
            {zones.map((z) => (
              <option key={z.zone_code} value={z.zone_code}>{z.zone_name}</option>
            ))}
          </select>
        </div>
      </Card>

      {isLoading ? (
        <Card padding={32}>
          <div className="text-center">
            <Icons.Spinner className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-4" />
            <p className="text-stone-500">Loading cameras...</p>
          </div>
        </Card>
      ) : cameras.length === 0 ? (
        <EmptyState
          icon={<Icons.Video size={40} />}
          title="No cameras configured"
          description="Add your first camera to start monitoring"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cameras.map((camera) => (
            <CameraCard
              key={camera.camera_id}
              camera={camera}
              buildings={buildings}
              onEdit={() => handleEdit(camera)}
              onDelete={() => handleDelete(camera)}
              onToggle={() => handleToggle(camera)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <CameraModal
          camera={editingCamera}
          zones={zones}
          buildings={buildings}
          detectionTypes={detectionTypes}
          onClose={() => { setShowModal(false); setEditingCamera(null); }}
          onSave={(data) => {
            if (editingCamera) {
              updateCameraMutation.mutate({ id: editingCamera.camera_id, data });
            } else {
              createCameraMutation.mutate(data);
            }
          }}
          isLoading={createCameraMutation.isPending || updateCameraMutation.isPending}
        />
      )}
    </div>
  );
};

const CameraCard = ({ camera, buildings, onEdit, onDelete, onToggle }) => {
  const building = buildings.find(b => b.id === camera.building_id);

  return (
    <Card hoverable className={!camera.is_active ? 'opacity-60' : ''}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${camera.is_online ? 'bg-green-50' : 'bg-stone-100'}`}>
              <Icons.Video className={`h-6 w-6 ${camera.is_online ? 'text-green-600' : 'text-stone-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-stone-900">{camera.name}</h3>
              <p className="text-sm text-stone-500">{camera.camera_id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {camera.is_online ? (
              <Badge tone="success"><Icons.Wifi size={12} className="inline mr-1" />Online</Badge>
            ) : (
              <Badge tone="danger"><Icons.WifiOff size={12} className="inline mr-1" />Offline</Badge>
            )}
          </div>
        </div>

        <div className="text-sm text-stone-600 mb-3">
          <div className="flex items-center space-x-2">
            <Icons.MapPin className="h-4 w-4 text-stone-400" />
            <span>{building?.name || 'No building'} {camera.floor ? `- Floor ${camera.floor}` : ''}</span>
          </div>
          {camera.zone && (
            <div className="ml-6 text-xs text-stone-500 capitalize">{camera.zone.replace(/_/g, ' ')}</div>
          )}
        </div>

        <div className="flex flex-wrap gap-1 mb-3">
          {camera.person_detection_enabled && <Badge tone="info">Person</Badge>}
          {camera.lpr_enabled && <Badge tone="violet">LPR</Badge>}
          {camera.fire_escape_monitoring && <Badge tone="warning">Fire Escape</Badge>}
          {camera.curfew_monitoring && <Badge tone="warning">Curfew</Badge>}
          {camera.alert_enabled && <Badge tone="success">Alerts</Badge>}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          {camera.is_active
            ? <Badge tone="success">Active</Badge>
            : <Badge tone="neutral">Inactive</Badge>
          }
          <div className="flex items-center space-x-1">
            <button
              onClick={onToggle}
              className="p-1.5 text-stone-400 hover:text-stone-600 rounded"
              title={camera.is_active ? 'Deactivate' : 'Activate'}
            >
              {camera.is_active ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 text-teal-500 hover:text-teal-700 rounded"
              title="Edit"
            >
              <Icons.Edit size={16} />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-red-500 hover:text-red-700 rounded"
              title="Delete"
            >
              <Icons.Trash size={16} />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const CameraModal = ({ camera, zones, buildings, detectionTypes, onClose, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    camera_id: camera?.camera_id || '',
    name: camera?.name || '',
    description: camera?.description || '',
    building_id: camera?.building_id || '',
    floor: camera?.floor || '',
    zone: camera?.zone || '',
    location_details: camera?.location_details || '',
    rtsp_url: camera?.rtsp_url || '',
    http_url: camera?.http_url || '',
    username: camera?.username || '',
    password: '',
    camera_type: camera?.camera_type || 'ip',
    resolution: camera?.resolution || '1080p',
    fps: camera?.fps || 15,
    detection_enabled: camera?.detection_enabled ?? true,
    detection_types: camera?.detection_types || ['person'],
    sensitivity: camera?.sensitivity || 0.5,
    lpr_enabled: camera?.lpr_enabled || false,
    person_detection_enabled: camera?.person_detection_enabled ?? true,
    fire_escape_monitoring: camera?.fire_escape_monitoring || false,
    curfew_monitoring: camera?.curfew_monitoring || false,
    curfew_start_time: camera?.curfew_start_time || '22:00',
    curfew_end_time: camera?.curfew_end_time || '06:00',
    alert_enabled: camera?.alert_enabled ?? true,
    alert_channels: camera?.alert_channels || ['in_app', 'email'],
    alert_cooldown_seconds: camera?.alert_cooldown_seconds || 60,
    recording_enabled: camera?.recording_enabled || false,
    recording_retention_days: camera?.recording_retention_days || 30,
  });

  const [activeTab, setActiveTab] = useState('basic');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...arr, value] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (data.building_id === '') delete data.building_id;
    if (data.floor === '') delete data.floor;
    else data.floor = parseInt(data.floor);
    data.fps = parseInt(data.fps);
    data.sensitivity = parseFloat(data.sensitivity);
    data.alert_cooldown_seconds = parseInt(data.alert_cooldown_seconds);
    data.recording_retention_days = parseInt(data.recording_retention_days);
    if (!data.password) delete data.password;
    onSave(data);
  };

  const tabs = [
    { id: 'basic', name: 'Basic Info' },
    { id: 'connection', name: 'Connection' },
    { id: 'detection', name: 'Detection' },
    { id: 'alerts', name: 'Alerts' },
  ];

  const LABEL = LABEL_CLS;
  const INPUT = INPUT_CLS;
  const SELECT = SELECT_CLS;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-stone-900">
            {camera ? 'Edit Camera' : 'Add New Camera'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg text-stone-500">
            <Icons.X size={18} />
          </button>
        </div>

        <div className="px-6 border-b border-stone-200">
          <nav className="flex space-x-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-700'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Camera ID *</label>
                  <input type="text" name="camera_id" value={formData.camera_id} onChange={handleChange}
                    className={INPUT} required disabled={!!camera} placeholder="e.g., CAM-001" />
                </div>
                <div>
                  <label className={LABEL}>Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange}
                    className={INPUT} required placeholder="e.g., Main Entrance Camera" />
                </div>
              </div>

              <div>
                <label className={LABEL}>Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange}
                  className={INPUT.replace('h-9 ', '')} rows={2} placeholder="Optional description..." />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={LABEL}>Building</label>
                  <select name="building_id" value={formData.building_id} onChange={handleChange} className={`w-full ${SELECT}`}>
                    <option value="">Select building...</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Floor</label>
                  <input type="number" name="floor" value={formData.floor} onChange={handleChange}
                    className={INPUT} placeholder="1" />
                </div>
                <div>
                  <label className={LABEL}>Zone</label>
                  <select name="zone" value={formData.zone} onChange={handleChange} className={`w-full ${SELECT}`}>
                    <option value="">Select zone...</option>
                    {zones.map((z) => (
                      <option key={z.zone_code} value={z.zone_code}>{z.zone_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={LABEL}>Location Details</label>
                <input type="text" name="location_details" value={formData.location_details} onChange={handleChange}
                  className={INPUT} placeholder="e.g., Near elevator on left side" />
              </div>
            </div>
          )}

          {activeTab === 'connection' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Camera Type</label>
                  <select name="camera_type" value={formData.camera_type} onChange={handleChange} className={`w-full ${SELECT}`}>
                    <option value="ip">IP Camera</option>
                    <option value="rtsp">RTSP Stream</option>
                    <option value="http">HTTP Stream</option>
                    <option value="usb">USB Camera</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Resolution</label>
                  <select name="resolution" value={formData.resolution} onChange={handleChange} className={`w-full ${SELECT}`}>
                    <option value="480p">480p</option>
                    <option value="720p">720p (HD)</option>
                    <option value="1080p">1080p (Full HD)</option>
                    <option value="4k">4K (Ultra HD)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={LABEL}>RTSP URL</label>
                <input type="text" name="rtsp_url" value={formData.rtsp_url} onChange={handleChange}
                  className={INPUT} placeholder="rtsp://192.168.1.100:554/stream" />
              </div>

              <div>
                <label className={LABEL}>HTTP URL</label>
                <input type="text" name="http_url" value={formData.http_url} onChange={handleChange}
                  className={INPUT} placeholder="http://192.168.1.100/video" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Username</label>
                  <input type="text" name="username" value={formData.username} onChange={handleChange}
                    className={INPUT} placeholder="admin" />
                </div>
                <div>
                  <label className={LABEL}>Password</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange}
                    className={INPUT} placeholder={camera ? '(unchanged)' : 'Enter password'} />
                </div>
              </div>

              <div>
                <label className={LABEL}>Frame Rate (FPS)</label>
                <input type="number" name="fps" value={formData.fps} onChange={handleChange}
                  className={INPUT} min="1" max="60" />
              </div>
            </div>
          )}

          {activeTab === 'detection' && (
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" id="detection_enabled" name="detection_enabled"
                  checked={formData.detection_enabled} onChange={handleChange}
                  className="h-4 w-4 rounded accent-teal-600" />
                <span className="text-sm font-medium text-stone-700">Enable Detection Processing</span>
              </label>

              <div>
                <label className={LABEL}>Detection Sensitivity</label>
                <input type="range" name="sensitivity" value={formData.sensitivity} onChange={handleChange}
                  className="w-full accent-teal-600" min="0" max="1" step="0.1" />
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Low</span>
                  <span>{(formData.sensitivity * 100).toFixed(0)}%</span>
                  <span>High</span>
                </div>
              </div>

              <div className="border border-stone-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-stone-700">Detection Features</h4>

                <label className="flex items-center gap-2">
                  <input type="checkbox" name="person_detection_enabled"
                    checked={formData.person_detection_enabled} onChange={handleChange}
                    className="h-4 w-4 rounded accent-teal-600" />
                  <span className="text-sm flex items-center gap-1">
                    <Icons.User size={14} />Person Detection
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input type="checkbox" name="lpr_enabled"
                    checked={formData.lpr_enabled} onChange={handleChange}
                    className="h-4 w-4 rounded accent-teal-600" />
                  <span className="text-sm flex items-center gap-1">
                    <Icons.Truck size={14} />License Plate Recognition (LPR)
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input type="checkbox" name="fire_escape_monitoring"
                    checked={formData.fire_escape_monitoring} onChange={handleChange}
                    className="h-4 w-4 rounded accent-teal-600" />
                  <span className="text-sm flex items-center gap-1">
                    <Icons.Fire size={14} />Fire Escape Obstruction Monitoring
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input type="checkbox" name="curfew_monitoring"
                    checked={formData.curfew_monitoring} onChange={handleChange}
                    className="h-4 w-4 rounded accent-teal-600" />
                  <span className="text-sm flex items-center gap-1">
                    <Icons.Clock size={14} />Curfew Monitoring
                  </span>
                </label>

                {formData.curfew_monitoring && (
                  <div className="ml-6 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Curfew Start</label>
                      <input type="time" name="curfew_start_time" value={formData.curfew_start_time}
                        onChange={handleChange} className={INPUT} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Curfew End</label>
                      <input type="time" name="curfew_end_time" value={formData.curfew_end_time}
                        onChange={handleChange} className={INPUT} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" id="alert_enabled" name="alert_enabled"
                  checked={formData.alert_enabled} onChange={handleChange}
                  className="h-4 w-4 rounded accent-teal-600" />
                <span className="text-sm font-medium text-stone-700">Enable Alerts</span>
              </label>

              <div>
                <label className={LABEL}>Alert Channels</label>
                <div className="flex flex-wrap gap-2">
                  {['in_app', 'email', 'telegram', 'sms'].map((channel) => (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => handleArrayToggle('alert_channels', channel)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors border ${
                        formData.alert_channels.includes(channel)
                          ? 'bg-teal-50 text-teal-700 border-teal-200'
                          : 'bg-stone-100 text-stone-600 border-stone-200'
                      }`}
                    >
                      {formData.alert_channels.includes(channel) && <Icons.Check size={14} className="inline mr-1" />}
                      {channel.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={LABEL}>Alert Cooldown (seconds)</label>
                <input type="number" name="alert_cooldown_seconds" value={formData.alert_cooldown_seconds}
                  onChange={handleChange} className={INPUT} min="0" />
                <p className="text-xs text-stone-500 mt-1">Minimum time between alerts for the same event type</p>
              </div>

              <div className="border-t border-stone-200 pt-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" id="recording_enabled" name="recording_enabled"
                    checked={formData.recording_enabled} onChange={handleChange}
                    className="h-4 w-4 rounded accent-teal-600" />
                  <span className="text-sm font-medium text-stone-700">Enable Recording</span>
                </label>

                {formData.recording_enabled && (
                  <div className="mt-3">
                    <label className={LABEL}>Recording Retention (days)</label>
                    <input type="number" name="recording_retention_days" value={formData.recording_retention_days}
                      onChange={handleChange} className={INPUT} min="1" />
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-3">
          <Button kind="secondary" onClick={onClose}>Cancel</Button>
          <Button kind="primary" onClick={handleSubmit} loading={isLoading}>
            {camera ? 'Update Camera' : 'Create Camera'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CVSetup;
